import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  deleteSupplierInvoice,
  postSupplierInvoice,
  saveSupplierInvoice,
} from '@/renderer/services/inventorySupplierInvoiceService';
import type { SaveSupplierInvoiceRequest } from '@/renderer/types/inventory';
import { apiError, apiSuccess } from '@/renderer/util/notify';

export function useSaveSupplierInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: SaveSupplierInvoiceRequest) =>
      saveSupplierInvoice(request),
    onSuccess: (result, variables) => {
      if (!result.success) {
        apiError(result.error ?? 'Failed to save invoice');
        return;
      }
      apiSuccess(variables.id ? 'Invoice updated' : 'Invoice created');
      queryClient.invalidateQueries({
        queryKey: ['inventory', 'supplier-invoices-list'],
      });
      queryClient.invalidateQueries({
        queryKey: ['inventory', 'supplier-invoice-summary'],
      });
    },
    onError: (error: Error) => {
      apiError(error.message ?? 'Failed to save invoice');
    },
  });
}

export function usePostSupplierInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      companyId,
      invoiceId,
    }: {
      companyId: number;
      invoiceId: string;
    }) => postSupplierInvoice(companyId, invoiceId),
    onSuccess: (result) => {
      if (!result.success) {
        apiError(result.error ?? 'Failed to post invoice');
        return;
      }
      apiSuccess('Invoice posted — stock balances updated');
      queryClient.invalidateQueries({
        queryKey: ['inventory', 'supplier-invoices-list'],
      });
      queryClient.invalidateQueries({
        queryKey: ['inventory', 'supplier-invoice-summary'],
      });
      queryClient.invalidateQueries({
        queryKey: ['inventory', 'supplier-invoice-detail'],
      });
      queryClient.invalidateQueries({
        queryKey: ['inventory', 'stock-balances-list'],
      });
      queryClient.invalidateQueries({
        queryKey: ['inventory', 'stock-balance-summary'],
      });
      queryClient.invalidateQueries({
        queryKey: ['inventory', 'stock-movements-list'],
      });
      queryClient.invalidateQueries({
        queryKey: ['inventory', 'stock-movement-summary'],
      });
    },
    onError: (error: Error) => {
      apiError(error.message ?? 'Failed to post invoice');
    },
  });
}

export function useDeleteSupplierInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      companyId,
      invoiceId,
    }: {
      companyId: number;
      invoiceId: string;
    }) => deleteSupplierInvoice(companyId, invoiceId),
    onSuccess: (result) => {
      if (!result.success) {
        apiError(result.error ?? 'Failed to delete invoice');
        return;
      }
      apiSuccess('Draft invoice deleted');
      queryClient.invalidateQueries({
        queryKey: ['inventory', 'supplier-invoices-list'],
      });
      queryClient.invalidateQueries({
        queryKey: ['inventory', 'supplier-invoice-summary'],
      });
    },
    onError: (error: Error) => {
      apiError(error.message ?? 'Failed to delete invoice');
    },
  });
}
